import {
  Button,
  Flex,
  FormLabel,
  IconButton,
  Image,
  Progress,
  Text,
  useToast
} from '@chakra-ui/core'
import { useField } from 'formik'
import * as React from 'react'
import { Check } from 'react-feather'
import { get, SpaceProps } from 'styled-system'
import { ERROR_TOAST } from '../../../constants'
import { UploadFile, UploadFileEntity } from '../../../generated/graphql'
import { theme } from '../../../theme'
import { getImageByFileExtension } from '../../../utils'
import strapiHelpers from '../../../utils/strapiHelpers'
import { AddFileButton, FileWrapper, HiddenInput, Wrapper } from './styles'

const { colors } = theme

type FileUploaderProps = SpaceProps & {
  name: string
  placeholder?: string
  label?: string
  onUpload?: (id: string | string[]) => void
  isMulti?: boolean
  isDisabled?: boolean
  allowedFileTypes?: string[]
  autoUpload?: boolean
  isEditing?: boolean
}

type ProgressObject = {
  state: string
  percentage: number
}

type UploadProgress = {
  [key: string]: ProgressObject
}

const FileUploader: React.FC<FileUploaderProps> = ({
  name,
  placeholder,
  label,
  isMulti,
  isDisabled,
  allowedFileTypes,
  autoUpload,
  isEditing,
  ...rest
}) => {
  const [stateFiles, setStateFiles] = React.useState<File[]>([])
  const [uploading, setUploading] = React.useState(false)
  const [{ value }, meta, helpers] = useField<UploadFileEntity | UploadFileEntity[]>(name)
  const [uploadProgress, setUploadProgress] = React.useState<UploadProgress>({})

  const renderProgress = (fileName: string) => {
    const progress = uploadProgress[fileName]
    if (uploading) {
      return (
        <Progress
          left={0}
          right={0}
          bottom={0}
          height="2px"
          color="green"
          hasStripe={true}
          isAnimated={true}
          position="absolute"
          value={progress?.percentage || 0}
        />
      )
    }
  }

  const toast = useToast()

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files) {
      setStateFiles((prevFiles) => prevFiles?.concat(Array.from(files)))
    }
  }

  const progress = ({ loaded, total }: ProgressEvent, file: File): void => {
    const copy = { ...uploadProgress }
    copy[file.name] = {
      state: 'pending',
      percentage: Math.round((loaded * 100) / total)
    }
    setUploadProgress((prevProgress) => ({ ...prevProgress, ...copy }))
  }

  const handleUpload = async () => {
    const promises = stateFiles.map((file) => strapiHelpers.upload(file, progress))
    setUploading(true)
    try {
      const uploadArr = await Promise.all(promises)
      const uploads = isMulti
        ? [...uploadArr.map((upload) => upload.data[0]), ...(value instanceof Array ? value : [])]
        : uploadArr[0].data[0]
      setStateFiles([])
      helpers.setValue(uploads)
      setUploading(false)
    } catch (e) {
      setUploading(false)
      toast({
        description: 'Something went wrong while uploading your file.',
        ...ERROR_TOAST
      })
    }
  }

  function determineType(
    toBeDetermined: UploadFileEntity | UploadFileEntity[]
  ): toBeDetermined is UploadFileEntity {
    if (toBeDetermined instanceof Array) {
      return false
    }
    return true
  }

  React.useEffect(() => {
    if (isMulti && value && value instanceof Array) {
      setStateFiles(
        stateFiles.filter((e) =>
          value.some((f) => `${f?.attributes?.name}${f?.attributes?.ext}` !== e.name)
        )
      )
    } else if (value && determineType(value) && value?.attributes?.name) {
      setStateFiles(
        stateFiles.filter((e) => e.name.replace(/\.[^/.]+$/, '') !== `${value?.attributes?.name}`)
      )
    }
    // eslint-disable-next-line
  }, [value])

  React.useEffect(() => {
    if (autoUpload && stateFiles.length > 0) {
      handleUpload()
    }
    // eslint-disable-next-line
  }, [stateFiles.length])

  return (
    <Wrapper mb={4} {...rest}>
      {label && (
        <FormLabel htmlFor={label} fontWeight="normal" fontSize="sm" mb={2}>
          {label}
        </FormLabel>
      )}
      {value && value instanceof Array && (
        <Wrapper>
          {value.map((file: UploadFileEntity) => {
            const uploadedFile = file as UploadFile
            return file.__typename === 'UploadFileEntity' ? (
              <FileWrapper justify="space-between" p={4} key={file?.attributes?.name}>
                <Flex align="center" w="92%">
                  <Image
                    mr={3}
                    rounded="md"
                    width="50px"
                    height="auto"
                    src={
                      file?.attributes?.mime?.includes('image/')
                        ? file?.attributes?.url
                        : getImageByFileExtension(file?.attributes?.name || '')
                    }
                  />

                  <Text ml={2} fontSize="xs" isTruncated>
                    {file?.attributes?.name}
                  </Text>
                </Flex>
                {isEditing ? (
                  <IconButton
                    size="xs"
                    icon="close"
                    aria-label="Remove File"
                    onClick={() => {
                      const newFiles = value.filter((e) => e.id !== file.id)
                      helpers.setValue(newFiles)
                      setStateFiles(stateFiles.filter((e) => e.name !== file?.attributes?.name))
                    }}
                  />
                ) : (
                  <Check size={20} color={get(colors, 'green.500', 'green')} />
                )}
              </FileWrapper>
            ) : (
              <FileWrapper justify="space-between" p={4} key={uploadedFile?.name}>
                <Flex align="center" w="92%">
                  <Image
                    mr={3}
                    rounded="md"
                    width="50px"
                    height="auto"
                    src={
                      uploadedFile?.mime?.includes('image/')
                        ? uploadedFile?.url
                        : getImageByFileExtension(uploadedFile?.name || '')
                    }
                  />
                  <Text ml={2} fontSize="xs" isTruncated>
                    {uploadedFile?.name}
                  </Text>
                </Flex>
                {isEditing ? (
                  <IconButton
                    size="xs"
                    icon="close"
                    aria-label="Remove File"
                    onClick={() => {
                      const newFiles = value.filter((e) => e.id !== (uploadedFile as any).id)
                      helpers.setValue(newFiles)
                      setStateFiles(stateFiles.filter((e) => e.name !== uploadedFile?.name))
                    }}
                  />
                ) : (
                  <Check size={20} color={get(colors, 'green.500', 'green')} />
                )}
              </FileWrapper>
            )
          })}
        </Wrapper>
      )}
      {value && determineType(value) && value?.attributes?.name && (
        <Wrapper>
          <FileWrapper justify="space-between" p={4} key={value?.attributes?.name}>
            <Flex align="center" w="92%">
              <Image
                rounded="md"
                width="50px"
                height="auto"
                src={
                  value?.attributes?.mime?.includes('image/')
                    ? value?.attributes?.formats?.thumbnail?.url || value?.attributes?.url
                    : getImageByFileExtension(value?.attributes?.name || '')
                }
              />
              <Text ml={4} isTruncated fontSize="xs">
                {value?.attributes?.name}
              </Text>
            </Flex>
            <Check size={20} color={get(colors, 'green.500', 'green')} />
          </FileWrapper>
        </Wrapper>
      )}
      {stateFiles &&
        stateFiles.length > 0 &&
        stateFiles.map((file: File) => {
          const progress = uploadProgress[file.name]
          const isImage = file.type.includes('image/')

          return (
            <FileWrapper justify="space-between" p={4} key={file.name}>
              <Flex align="center" w="92%" position="relative">
                <Image
                  mr={3}
                  rounded="md"
                  width="50px"
                  height="auto"
                  src={
                    isImage
                      ? window.URL.createObjectURL(file)
                      : getImageByFileExtension(file.name || '')
                  }
                />

                <Text ml={4} isTruncated fontSize="xs">
                  {file.name}
                </Text>
              </Flex>
              {progress && progress.state === 'done' ? (
                <Check size={20} color={theme.colors.success[300]} />
              ) : (
                <Flex>
                  <IconButton
                    size="xs"
                    icon="close"
                    aria-label="Remove File"
                    onClick={() => setStateFiles(stateFiles.filter((e) => e.name !== file.name))}
                  />
                </Flex>
              )}
              {renderProgress(file.name)}
            </FileWrapper>
          )
        })}
      <Flex mt={!!label ? 0 : 2} justifyContent="flex-end" width="100%" flexDirection="row">
        <AddFileButton
          isDisabled={isDisabled}
          htmlFor={name}
          fontSize="sm"
          fontWeight="normal"
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          mr={stateFiles && stateFiles.length > 0 ? 4 : 0}
        >
          <Text>{placeholder}</Text>
        </AddFileButton>
        {stateFiles && stateFiles.length > 0 && (
          <Button isLoading={uploading} flex={1} onClick={() => handleUpload()} fontSize="sm">
            <Text>Upload</Text>
          </Button>
        )}
      </Flex>
      <HiddenInput
        disabled={isDisabled}
        onChange={onChange}
        type="file"
        accept={allowedFileTypes?.join(',') || 'image/*,application/pdf'}
        multiple={isMulti}
        name={name}
        id={name}
      />
      {/* TODO: Figure out how to make this work with meta.touched condition */}
      {meta.error ? (
        <Text color="red.500" textAlign="left">
          {get(meta.error, 'id', '')}
        </Text>
      ) : null}
    </Wrapper>
  )
}

FileUploader.defaultProps = {
  placeholder: 'Add Files',
  isMulti: false
}

export default FileUploader
